// InstallAppPrompt.jsx — v119.213
// Guides mobile users to install the PWA (so it opens as a real app, not a
// website) and to enable push notifications once installed.
// - Android/Chrome: captures beforeinstallprompt → native install dialog.
// - iOS Safari: step-by-step "Add to Home Screen" instructions.
// - Standalone mode + no push permission: one-tap enable-notifications card.
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "./lib/api";
import { subscribeToPush } from "./lib/pwa";
import { zVar } from "./lib/zIndex";

const DISMISS_KEY = "tw-install-dismissed-at";
const DISMISS_DAYS = 7;

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)")?.matches ||
  window.navigator.standalone === true;

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);

export default function InstallAppPrompt() {
  const [visible, setVisible] = useState(false);
  const [deferred, setDeferred] = useState(null); // Android install event
  const [showIosSteps, setShowIosSteps] = useState(false);
  const [standalone] = useState(isStandalone());
  const [pushDone, setPushDone] = useState(
    typeof Notification !== "undefined" && Notification.permission === "granted"
  );
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    const fresh = Date.now() - dismissedAt < DISMISS_DAYS * 86400000;
    const mobile = window.innerWidth < 768;
    if (!mobile || fresh) return;
    if (standalone && pushDone) return;
    setVisible(true);
    const onBip = (e) => { e.preventDefault(); setDeferred(e); };
    window.addEventListener("beforeinstallprompt", onBip);
    return () => window.removeEventListener("beforeinstallprompt", onBip);
  }, [standalone, pushDone]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") {
        toast.success("📲 تم التثبيت! افتح «ثواني» من شاشتك الرئيسية");
        dismiss();
      }
      setDeferred(null);
    } else {
      setShowIosSteps((s) => !s);
    }
  };

  const enablePush = async () => {
    setBusy(true);
    try {
      const r = await subscribeToPush(api);
      if (r?.ok) {
        setPushDone(true);
        toast.success("🔔 الإشعارات مفعّلة — ستصلك تنبيهات التطبيق");
        dismiss();
      } else {
        toast.error("تعذّر تفعيل الإشعارات", { description: r?.reason || "حاول من إعدادات الجهاز" });
      }
    } catch (e) {
      toast.error("تعذّر تفعيل الإشعارات", {
        description: e?.message || "تأكد من السماح بالإشعارات في إعدادات الجهاز",
      });
    }
    setBusy(false);
  };

  if (!visible) return null;

  return (
    <div data-testid="install-app-prompt" dir="rtl"
         style={{
           position: "fixed", insetInline: 10, bottom: 74, zIndex: zVar('extra_60'),
           background: "linear-gradient(135deg,#4a2c1a 0%,#6b3f26 100%)",
           color: "#fff", borderRadius: 16, padding: "12px 14px",
           boxShadow: "0 8px 30px rgba(0,0,0,0.35)",
         }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <img src="/icon-thawany-96.png" alt="" style={{ width: 40, height: 40, borderRadius: 10, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          {!standalone ? (
            <>
              <div style={{ fontSize: 13.5, fontWeight: 800 }}>📲 ثبّت تطبيق ثواني</div>
              <div style={{ fontSize: 11, opacity: 0.85, marginTop: 2, lineHeight: 1.6 }}>
                يفتح كتطبيق حقيقي (بدون متصفح) وتصلك الإشعارات مباشرة.
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 13.5, fontWeight: 800 }}>🔔 فعّل إشعارات التطبيق</div>
              <div style={{ fontSize: 11, opacity: 0.85, marginTop: 2, lineHeight: 1.6 }}>
                تنبيهات الطلبات والمبيعات والاجتماعات تصلك فوراً.
              </div>
            </>
          )}
        </div>
        <button onClick={dismiss} data-testid="install-prompt-dismiss"
                style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff",
                         width: 26, height: 26, borderRadius: 999, cursor: "pointer", flexShrink: 0 }}>✕</button>
      </div>

      {showIosSteps && !standalone && (
        <div data-testid="ios-install-steps"
             style={{ marginTop: 10, background: "rgba(255,255,255,0.1)", borderRadius: 12,
                      padding: "10px 12px", fontSize: 11.5, lineHeight: 2 }}>
          <div>1️⃣ اضغط زر المشاركة <span style={{ fontSize: 14 }}>⎋</span> أسفل Safari</div>
          <div>2️⃣ اختر <b>«إضافة إلى الشاشة الرئيسية»</b> ➕</div>
          <div>3️⃣ اضغط <b>«إضافة»</b> — وافتح ثواني من أيقونته ☕</div>
          <div style={{ opacity: 0.75, fontSize: 10.5 }}>💡 بعد الفتح من الأيقونة، فعّل الإشعارات من نفس هذه الرسالة.</div>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        {!standalone ? (
          <button onClick={install} data-testid="install-app-btn"
                  style={{ flex: 1, background: "#D4AF37", border: "none", color: "#3a2b1a",
                           fontWeight: 800, fontSize: 13, padding: "9px 0", borderRadius: 12,
                           cursor: "pointer", fontFamily: "inherit" }}>
            {deferred ? "📲 تثبيت الآن" : isIOS() ? (showIosSteps ? "إخفاء الخطوات" : "📖 طريقة التثبيت (iPhone)") : "📖 طريقة التثبيت"}
          </button>
        ) : (
          <button onClick={enablePush} disabled={busy} data-testid="enable-push-btn"
                  style={{ flex: 1, background: "#D4AF37", border: "none", color: "#3a2b1a",
                           fontWeight: 800, fontSize: 13, padding: "9px 0", borderRadius: 12,
                           cursor: "pointer", fontFamily: "inherit", opacity: busy ? 0.6 : 1 }}>
            {busy ? "⏳ جاري التفعيل..." : "🔔 تفعيل الإشعارات"}
          </button>
        )}
        <button onClick={dismiss}
                style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.3)",
                         color: "#fff", fontSize: 12, padding: "0 14px", borderRadius: 12,
                         cursor: "pointer", fontFamily: "inherit" }}>لاحقاً</button>
      </div>
    </div>
  );
}
